import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SettingsIcon from '@mui/icons-material/Settings';

/**
 * Admin page for managing e-commerce platforms globally.
 * Admins can:
 * - Add new platforms (ikas, magento, ticimax, etc.)
 * - Define credential fields for each platform
 * - Enable/disable platforms
 * - View all store integrations across users
 */
const AdminPlatformsPage = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Platform dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [platformName, setPlatformName] = useState('');
  const [platformCode, setPlatformCode] = useState('');
  const [platformDescription, setPlatformDescription] = useState('');
  const [platformActive, setPlatformActive] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Credential fields dialog
  const [fieldsDialogOpen, setFieldsDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentialFields, setCredentialFields] = useState([]);
  const [newField, setNewField] = useState({ field_key: '', field_label: '', field_type: 'text', placeholder: '', help_text: '', is_required: true });

  useEffect(() => {
    loadPlatforms();
  }, []);

  const loadPlatforms = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_available_platforms');
      if (error) throw error;
      setPlatforms(data || []);
    } catch (e) {
      console.error('Failed to load platforms:', e);
      setError('Failed to load platforms');
    } finally {
      setLoading(false);
    }
  };

  const loadCredentialFields = async (platformId) => {
    try {
      const { data, error } = await supabase
        .from('platform_credential_fields')
        .select('*')
        .eq('platform_id', platformId)
        .order('display_order');
      
      if (error) {
        // Try with schema
        const { data: data2 } = await supabase.rpc('get_platform_credential_fields', {
          p_platform_code: platforms.find(p => p.id === platformId)?.code
        });
        setCredentialFields(data2 || []);
      } else {
        setCredentialFields(data || []);
      }
    } catch (e) {
      console.error('Failed to load credential fields:', e);
    }
  };

  const handleOpenDialog = (platform = null) => {
    if (platform) {
      setEditingPlatform(platform);
      setPlatformName(platform.name);
      setPlatformCode(platform.code);
      setPlatformDescription(platform.description || '');
      setPlatformActive(platform.is_active);
    } else {
      setEditingPlatform(null);
      setPlatformName('');
      setPlatformCode('');
      setPlatformDescription('');
      setPlatformActive(true);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlatform(null);
  };

  const handleSavePlatform = async () => {
    if (!platformName.trim() || !platformCode.trim()) {
      setError('Name and code are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingPlatform) {
        // Update existing
        const { error } = await supabase
          .from('platforms')
          .update({
            name: platformName,
            description: platformDescription,
            is_active: platformActive,
          })
          .eq('id', editingPlatform.id);
        
        if (error) throw error;
        setSuccess('Platform updated successfully');
      } else {
        // Create new
        const { error } = await supabase
          .from('platforms')
          .insert({
            name: platformName,
            code: platformCode.toLowerCase().replace(/\s+/g, '_'),
            description: platformDescription,
            is_active: platformActive,
          });
        
        if (error) throw error;
        setSuccess('Platform created successfully');
      }
      
      handleCloseDialog();
      await loadPlatforms();
    } catch (e) {
      setError(e.message || 'Failed to save platform');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFieldsDialog = async (platform) => {
    setSelectedPlatform(platform);
    await loadCredentialFields(platform.id);
    setFieldsDialogOpen(true);
  };

  const handleCloseFieldsDialog = () => {
    setFieldsDialogOpen(false);
    setSelectedPlatform(null);
    setCredentialFields([]);
    setNewField({ field_key: '', field_label: '', field_type: 'text', placeholder: '', help_text: '', is_required: true });
  };

  const handleAddField = async () => {
    if (!newField.field_key || !newField.field_label) {
      setError('Field key and label are required');
      return;
    }

    try {
      const { error } = await supabase
        .from('platform_credential_fields')
        .insert({
          platform_id: selectedPlatform.id,
          field_key: newField.field_key,
          field_label: newField.field_label,
          field_type: newField.field_type,
          placeholder: newField.placeholder,
          help_text: newField.help_text,
          is_required: newField.is_required,
          display_order: credentialFields.length,
        });
      
      if (error) throw error;
      
      await loadCredentialFields(selectedPlatform.id);
      setNewField({ field_key: '', field_label: '', field_type: 'text', placeholder: '', help_text: '', is_required: true });
      setSuccess('Field added successfully');
    } catch (e) {
      setError(e.message || 'Failed to add field');
    }
  };

  const handleDeleteField = async (fieldId) => {
    try {
      const { error } = await supabase
        .from('platform_credential_fields')
        .delete()
        .eq('id', fieldId);
      
      if (error) throw error;
      
      await loadCredentialFields(selectedPlatform.id);
      setSuccess('Field deleted');
    } catch (e) {
      setError(e.message || 'Failed to delete field');
    }
  };

  const handleTogglePlatform = async (platform) => {
    try {
      const { error } = await supabase
        .from('platforms')
        .update({ is_active: !platform.is_active })
        .eq('id', platform.id);
      
      if (error) throw error;
      await loadPlatforms();
    } catch (e) {
      setError(e.message || 'Failed to toggle platform');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Platform Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage e-commerce platforms and their credential requirements. Users will see these platforms when connecting their stores.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Add Platform Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Platform
        </Button>
      </Box>

      {/* Platforms Grid */}
      <Grid container spacing={3}>
        {platforms.map((platform) => (
          <Grid item xs={12} sm={6} md={4} key={platform.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box display="flex" alignItems="center" gap={1}>
                    <StorefrontIcon color="primary" />
                    <Typography variant="h6">{platform.name}</Typography>
                  </Box>
                  <Switch
                    checked={platform.is_active}
                    onChange={() => handleTogglePlatform(platform)}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Code: <code>{platform.code}</code>
                </Typography>
                
                {platform.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {platform.description}
                  </Typography>
                )}
                
                <Box sx={{ mt: 2 }}>
                  {platform.has_adapter ? (
                    <Chip label="Has Credentials" color="success" size="small" />
                  ) : (
                    <Chip label="No Credentials Defined" color="warning" size="small" />
                  )}
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenDialog(platform)}>
                  Edit
                </Button>
                <Button size="small" startIcon={<SettingsIcon />} onClick={() => handleOpenFieldsDialog(platform)}>
                  Credentials
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {platforms.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', mt: 3 }}>
          <Typography color="text.secondary">
            No platforms configured yet. Click "Add Platform" to create one.
          </Typography>
        </Paper>
      )}

      {/* Platform Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPlatform ? 'Edit Platform' : 'Add New Platform'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Platform Name"
              placeholder="e.g., ikas, Magento, Shopify"
              value={platformName}
              onChange={(e) => setPlatformName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Platform Code"
              placeholder="e.g., ikas, magento, shopify"
              value={platformCode}
              onChange={(e) => setPlatformCode(e.target.value)}
              sx={{ mb: 2 }}
              required
              disabled={!!editingPlatform}
              helperText="Unique identifier (cannot be changed after creation)"
            />
            <TextField
              fullWidth
              label="Description"
              placeholder="Brief description of this platform"
              value={platformDescription}
              onChange={(e) => setPlatformDescription(e.target.value)}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <FormControlLabel
              control={<Switch checked={platformActive} onChange={(e) => setPlatformActive(e.target.checked)} />}
              label="Active (visible to users)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSavePlatform} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credential Fields Dialog */}
      <Dialog open={fieldsDialogOpen} onClose={handleCloseFieldsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Credential Fields for {selectedPlatform?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the credentials users need to provide when connecting their {selectedPlatform?.name} store.
          </Typography>

          {/* Existing Fields */}
          {credentialFields.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field Key</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {credentialFields.map((field) => (
                    <TableRow key={field.id || field.field_key}>
                      <TableCell><code>{field.field_key}</code></TableCell>
                      <TableCell>{field.field_label}</TableCell>
                      <TableCell>{field.field_type}</TableCell>
                      <TableCell>{field.is_required ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDeleteField(field.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Add New Field</Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Field Key"
                placeholder="e.g., client_id"
                value={newField.field_key}
                onChange={(e) => setNewField({ ...newField, field_key: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Label"
                placeholder="e.g., Client ID"
                value={newField.field_label}
                onChange={(e) => setNewField({ ...newField, field_label: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Type"
                value={newField.field_type}
                onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="text">Text</option>
                <option value="password">Password</option>
                <option value="url">URL</option>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Placeholder"
                value={newField.placeholder}
                onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newField.is_required}
                    onChange={(e) => setNewField({ ...newField, is_required: e.target.checked })}
                    size="small"
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Help Text"
                placeholder="Instructions for users"
                value={newField.help_text}
                onChange={(e) => setNewField({ ...newField, help_text: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddField}>
                Add Field
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFieldsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPlatformsPage;
